// src/pages/TechnicianFormPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Divider, CircularProgress, Card, CardContent, Chip, Stack, Alert } from '@mui/material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { getData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';
import TechnicianTabsWrapper from '../components/TechnicianTabsWrapper';

const TechnicianFormPage = ({ user }) => {
    const { t } = useI18n();
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const loadReport = async () => {
            try {
                const activeReport = await getData('active_technician_report');
                if (activeReport) {
                    setReport(activeReport);
                } else {
                    setError(t('technician_no_active_report'));
                    setTimeout(() => navigate('/technician-report'), 2000);
                }
            } catch (err) {
                setError(t('technician_report_error'));
            } finally {
                setLoading(false);
            }
        };
        loadReport();
    }, [navigate, t]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!report) {
        return null;
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
            {/* Header Card */}
            <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                        <Box>
                            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
                                {t('tech_form_title')}
                            </Typography>
                            <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                                <Chip 
                                    label={`${t('labels_date')}: ${format(new Date(report.date), 'PPPP', { locale: es })}`}
                                    color="primary"
                                    variant="outlined"
                                />
                                <Chip 
                                    label={`${t('common_technician')}: ${report.createdBy}`}
                                    color="secondary"
                                    variant="outlined"
                                />
                                <Chip 
                                    label={`${t('common_status')}: ${report.status === 'open' ? t('status_active') : t('status_completed')}`}
                                    color={report.status === 'open' ? 'warning' : 'success'}
                                    variant="filled"
                                />
                            </Stack>
                        </Box>
                        
                        <Stack direction="row" spacing={1}>
                            <Chip 
                                label={t('tech_report_id')}
                                variant="outlined"
                                sx={{ fontWeight: 'bold' }}
                            />
                            <Chip 
                                label={report.id}
                                color="info"
                                variant="filled"
                            />
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {/* Tabs Section */}
            <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <CardContent sx={{ p: 0 }}>
                    <TechnicianTabsWrapper reportData={report} user={user} />
                </CardContent>
            </Card>
        </Box>
    );
};

export default TechnicianFormPage;